import React, { useEffect, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const bundled = src => typeof src === 'string' && /^\/assets\//.test(src);
// Resolve already cached media, never download an image just to read an offline post.
export function useOfflineImage(src) {
  const [resolved,setResolved] = useState(null);
  useEffect(() => {
    let active = true, objectUrl;
    setResolved(null);
    if (!src || bundled(src)) return undefined;
    (async () => {
      try {
        const url = new URL(src,window.location.origin);
        if (!['http:','https:'].includes(url.protocol) || !window.caches) return;
        const response = await window.caches.match(url.href);
        if (!response) return;
        if (response.type === 'opaque') {
          // The app worker serves these image URLs cache-first. Opaque responses
          // cannot become blobs, and API/Firebase routes deliberately bypass it.
          const cachedImageRoute = /\.(png|jpg|jpeg|gif|webp|svg|ico)$/i.test(url.pathname)
            || ['assets.amposter.com','post-img-uploader.amposter.com'].includes(url.hostname)
            || /imagedelivery\.net|cloudflare/.test(url.hostname);
          if (active && navigator.serviceWorker?.controller && cachedImageRoute
              && url.hostname !== 'api.amposter.com' && !url.pathname.startsWith('/api/')
              && !/googleapis\.com|firebaseio\.com|firebasedatabase\.app/.test(url.hostname)) setResolved({src,url:src});
          return;
        }
        const blob = await response.blob();
        if (!active || !blob.size || !blob.type.startsWith('image/')) return;
        objectUrl = URL.createObjectURL(blob);setResolved({src,url:objectUrl});
      } catch { /* missing media uses a stable placeholder */ }
    })();
    return () => { active=false;if(objectUrl)URL.revokeObjectURL(objectUrl); };
  },[src]);
  return bundled(src) ? src : resolved && resolved.src === src ? resolved.url : null;
}

export default function OfflineImage({ src, alt = '', className = '' }) {
  const url = useOfflineImage(src), { language } = useLanguage();
  const [failed,setFailed] = useState(null);
  if (!url || failed === url) return <span className={`cached-media-missing ${className}`} role="img" aria-label={language === 'ar' ? 'الصورة غير محفوظة على الجهاز' : 'Image not saved on this device'}><ImageOff size={24}/><small>{language === 'ar' ? 'الصورة غير محفوظة' : 'Image not saved'}</small></span>;
  return <img src={url} alt={alt} className={className} loading="lazy" onError={()=>setFailed(url)}/>;
}
